// Aignoz — Phase 5 shell + gated routing.
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Shell, LayoutProvider } from "@/shell";
import { CommandCenter, NewProject, ProjectsList, DemoMode, AgentsCatalog, ProblemLibrary } from "@/home";
import { VentureWorkspace } from "@/workspace";
import { DiscoverHome, InnovationMatrix, OpportunityHeatmap } from "@/discover";
import { PricingPage, ROICalculator, EnterpriseInquiry } from "@/pricing";
import { PlatformPage } from "@/platform";
import FactoryPage from "@/factory";
import LandingPage from "@/landing";
import LoginPage from "@/login";
import MatrixPage from "@/matrix";
import SourcesPage from "@/sources";
import CapTablePage from "@/captable";
import TemplatesPage from "@/templates";
import { PersonaProvider, ProfilePage } from "@/persona";
import { AuthProvider, useAuth } from "@/auth-context";
import "@/App.css";

function RequireAuth({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-[#0b0c10]">
        <div className="text-white/50 text-sm mono">Loading…</div>
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return children;
}

export default function App() {
  useEffect(() => { document.documentElement.classList.add("dark"); }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <PersonaProvider>
          <LayoutProvider>
            <Routes>
              {/* Public */}
              <Route path="/login" element={<LoginPage />} />

              {/* Gated app shell */}
              <Route element={<RequireAuth><Shell /></RequireAuth>}>
                <Route path="/" element={<LandingPage />} />
                <Route path="/home" element={<CommandCenter />} />
                <Route path="/discover" element={<DiscoverHome />} />
                <Route path="/discover/matrix" element={<InnovationMatrix />} />
                <Route path="/discover/heatmap" element={<OpportunityHeatmap />} />
                <Route path="/projects" element={<ProjectsList />} />
                <Route path="/workspace" element={<ProjectsList />} />
                <Route path="/projects/new" element={<NewProject />} />
                <Route path="/demo" element={<DemoMode />} />
                <Route path="/library" element={<ProblemLibrary />} />
                <Route path="/agents" element={<AgentsCatalog />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/platform" element={<PlatformPage />} />
                <Route path="/factory" element={<FactoryPage />} />
                <Route path="/matrix" element={<MatrixPage />} />
                <Route path="/sources" element={<SourcesPage />} />
                <Route path="/captable" element={<CapTablePage />} />
                <Route path="/templates" element={<TemplatesPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/pricing/roi-calculator" element={<ROICalculator />} />
                <Route path="/pricing/enterprise-inquiry" element={<EnterpriseInquiry />} />
                <Route path="/projects/:id" element={<Navigate to="overview" replace />} />
                <Route path="/projects/:id/:tab" element={<VentureWorkspace />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </LayoutProvider>
        </PersonaProvider>
      </AuthProvider>
      <Toaster position="top-right" theme="dark" />
    </BrowserRouter>
  );
}
